export type AssessmentSubQuestion = {
  sub_id: string | number;
  sub_question?: string;
};

export type AssessmentQuestion = {
  parent_id: string | number;
  question?: string;
  assessment_question_options?: AssessmentSubQuestion[];
};

export type CommentedRow = {
  key: string;
  code: string | null;
  question: string;
  subQuestion?: string;
  status: string;
  comment: string;
};

const stripHtml = (html = '') =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Question titles start with a code such as "WA1", "PA2" or "WA 3".
export const getQuestionCode = (title = '') => {
  const match = stripHtml(title).match(/^([A-Za-z]{2,})\s?(\d+)\b/);
  return match ? `${match[1].toUpperCase()}${match[2]}` : null;
};

const hasText = (value?: string | null) => !!value && value.trim() !== '';

const getEntries = (question: AssessmentQuestion) => {
  const options = question.assessment_question_options;
  if (options && options.length) {
    return options.map((sub) => ({
      key: `${question.parent_id}_${sub.sub_id}`,
      subQuestion: stripHtml(sub.sub_question),
    }));
  }
  return [{ key: `${question.parent_id}`, subQuestion: undefined }];
};

export const buildOralRecoveryRemark = (
  questions: AssessmentQuestion[],
  comments: Record<string, string> = {}
) => {
  const codes: string[] = [];
  questions.forEach((question) => {
    const code = getQuestionCode(question.question);
    if (!code || codes.includes(code)) return;
    if (getEntries(question).some(({ key }) => hasText(comments[key]))) {
      codes.push(code);
    }
  });
  return codes.length ? `OR Done for ${codes.join(', ')}` : '';
};

export const findCommentedRows = (
  questions: AssessmentQuestion[],
  status: Record<string, string> = {},
  comments: Record<string, string> = {}
): CommentedRow[] =>
  questions.flatMap((question) =>
    getEntries(question)
      .filter(({ key }) => hasText(comments[key]))
      .map(({ key, subQuestion }) => ({
        key,
        code: getQuestionCode(question.question),
        question: stripHtml(question.question),
        subQuestion: subQuestion || undefined,
        status: status[key] ?? '',
        comment: comments[key].trim(),
      }))
  );

export const findNycWithRemarks = (
  questions: AssessmentQuestion[],
  status: Record<string, string> = {},
  comments: Record<string, string> = {}
) => findCommentedRows(questions, status, comments).filter((row) => row.status === 'NYC');
